import { callTool } from "../api";
import { firstNameFromUserKey, ratingLineage } from "../ratings";
import { PRIORITIES, priorityIdsFromStored, userIdsMatch } from "./logic";
import type { DebriefCard, DebriefFollowup, DebriefNote, DebriefPatch, LeadRating } from "./types";

const PAGE_LIMIT = 200;
const MAX_PAGES = 40;

interface SearchRow {
  contact_id?: string;
  account_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  phone_direct?: string | null;
  profile_url?: string | null;
  account_name?: string | null;
  segment_primary?: string | null;
  segment?: string | null;
  attribution?: string[] | null;
  event_contact_no?: number | string | null;
  event_contact_display?: string | null;
  updated_at?: string | null;
  priority?: boolean;
  account_priority?: boolean;
  key_contact?: boolean;
  lead_rating?: number | string | null;
  owner_user?: string | null;
  account_owner_user?: string | null;
}

interface SearchPage {
  results?: SearchRow[];
  has_more?: boolean;
  next_cursor?: string | null;
}

interface NoteHit {
  note_id?: string;
  body?: string | null;
  actor?: string | null;
  created_at?: string | null;
}

interface FollowupHit {
  followup_id?: string;
  title?: string | null;
  due_at?: string | null;
  status?: string | null;
  created_at?: string | null;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function eventContactNo(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

function priorityIdsFromJson(value: unknown): string[] {
  return priorityIdsFromStored(value);
}

export function priorityLabels(ids: string[]): string[] {
  return PRIORITIES.filter((priority) => ids.includes(priority.id)).map((priority) => priority.label);
}

function toCard(row: SearchRow, campaignKey: string, reviewed: Set<string>): DebriefCard | null {
  const contactId = text(row.contact_id);
  if (!contactId) return null;
  const eventContactId = eventContactNo(row.event_contact_no);
  const onShow = eventContactId != null;
  const fullName = [text(row.first_name), text(row.last_name)].filter(Boolean).join(" ") || text(row.email) || "Unknown contact";
  const display = text(row.event_contact_display);
  return {
    contactId,
    eventContactId,
    eventContactDisplay: display || null,
    eventKey: onShow ? campaignKey : null,
    accountId: text(row.account_id),
    accountName: text(row.account_name) || text(row.company),
    fullName,
    title: text(row.title),
    photoUrl: null,
    businessCardUrl: null,
    businessCardFrontUrl: null,
    businessCardBackUrl: null,
    email: text(row.email) || null,
    phoneE164: text(row.phone_direct) || text(row.phone) || null,
    linkedinUrl: text(row.profile_url) || null,
    profileSummary: "",
    segment: text(row.segment_primary) || text(row.segment),
    rating: row.lead_rating == null ? 0 : asLeadRating(row.lead_rating),
    notes: [],
    followups: [],
    priorityIds: [],
    familiarUserIds: [],
    ...(row.account_priority === undefined && row.priority === undefined
      ? {}
      : { priorityFollowup: (row.account_priority ?? row.priority) === true }),
    ...(row.key_contact === undefined ? {} : { keyContact: row.key_contact === true }),
    ...(row.account_owner_user === undefined && row.owner_user === undefined
      ? {}
      : { ownerUserId: text(row.account_owner_user ?? row.owner_user) || null }),
    attribution: Array.isArray(row.attribution) ? row.attribution.filter((item) => typeof item === "string" && item.trim()) : [],
    campaignKey: onShow ? campaignKey : null,
    reviewed: reviewed.has(contactId),
    updatedAt: text(row.updated_at) || new Date().toISOString(),
  };
}

/** Keep a photo, notes, and follow-up that already arrived when a later search page rebuilds the roster. */
export function mergeSearchRoster(previous: DebriefCard[], incoming: DebriefCard[], hydratedIds: Set<string>): DebriefCard[] {
  const prior = new Map(previous.map((card) => [card.contactId, card]));
  return incoming.map((next) => {
    const old = prior.get(next.contactId);
    if (!old) return next;
    const keepDetail = hydratedIds.has(next.contactId);
    return {
      ...next,
      photoUrl: old.photoUrl ?? next.photoUrl,
      businessCardUrl: old.businessCardUrl ?? next.businessCardUrl,
      businessCardFrontUrl: old.businessCardFrontUrl ?? next.businessCardFrontUrl,
      businessCardBackUrl: old.businessCardBackUrl ?? next.businessCardBackUrl,
      notes: keepDetail || old.notes.length > 0 ? old.notes : next.notes,
      followups: keepDetail || old.followups.length > 0 ? old.followups : next.followups,
      priorityIds: keepDetail || old.priorityIds.length > 0 ? old.priorityIds : next.priorityIds,
      rating: old.rating || next.rating,
      familiarUserIds: old.familiarUserIds.length > 0 ? old.familiarUserIds : next.familiarUserIds,
      priorityFollowup: next.priorityFollowup !== undefined ? next.priorityFollowup : old.priorityFollowup,
      keyContact: next.keyContact !== undefined ? next.keyContact : old.keyContact,
      ownerUserId: next.ownerUserId !== undefined ? next.ownerUserId : old.ownerUserId,
      segment: old.segment || next.segment,
      reviewed: old.reviewed || next.reviewed,
    };
  });
}

function absorbPage(byId: Map<string, DebriefCard>, data: SearchPage, campaignKey: string, reviewed: Set<string>) {
  for (const row of data.results ?? []) {
    const card = toCard(row, campaignKey, reviewed);
    if (!card || card.eventContactId == null) continue;
    const prev = byId.get(card.contactId);
    if (!prev || (prev.eventContactId == null && card.eventContactId != null)) byId.set(card.contactId, card);
  }
}

/** Live numbered journeys for this show. Each card's contact id is the one on that journey row. */
export async function loadIntelDebrief(
  campaignKey: string,
  reviewed: Set<string>,
  onBatch?: (cards: DebriefCard[]) => void,
): Promise<DebriefCard[]> {
  const byId = new Map<string, DebriefCard>();
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data = await callTool<SearchPage>("contact_search", {
      campaign_key: campaignKey,
      has_event_contact: true,
      include_do_not_contact: true,
      limit: PAGE_LIMIT,
      cursor,
    });
    const results = data.results ?? [];
    if (results.length > 0 && !results.some((row) => eventContactNo(row.event_contact_no) != null)) {
      throw new Error("contact_search did not limit the roster to numbered journeys.");
    }
    absorbPage(byId, data, campaignKey, reviewed);
    onBatch?.([...byId.values()]);
    if (!data.has_more || !data.next_cursor) return [...byId.values()];
    cursor = data.next_cursor;
  }
  throw new Error("contact_search did not finish. Refusing to show a partial roster.");
}

export function unionSearchRoster(previous: DebriefCard[], incoming: DebriefCard[], hydratedIds: Set<string>): DebriefCard[] {
  const incomingIds = new Set(incoming.map((card) => card.contactId));
  const kept = previous.filter((card) => !incomingIds.has(card.contactId));
  return mergeSearchRoster(previous, [...incoming, ...kept], hydratedIds);
}

export async function searchDebriefContacts(campaignKey: string, query: string, reviewed: Set<string>): Promise<DebriefCard[]> {
  const data = await callTool<SearchPage>("contact_search", {
    campaign_key: campaignKey,
    query,
    include_do_not_contact: true,
    limit: PAGE_LIMIT,
  });
  return (data.results ?? []).flatMap((row) => {
    const card = toCard(row, campaignKey, reviewed);
    return card ? [card] : [];
  });
}

export async function loadAccountContacts(accountId: string, campaignKey: string, reviewed: Set<string>): Promise<DebriefCard[]> {
  const cards: DebriefCard[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 5; page += 1) {
    const data = await callTool<SearchPage>("contact_search", {
      account_id: accountId,
      campaign_key: campaignKey,
      include_do_not_contact: true,
      limit: PAGE_LIMIT,
      cursor,
    });
    for (const row of data.results ?? []) {
      const card = toCard(row, campaignKey, reviewed);
      if (card) cards.push(card);
    }
    if (!data.has_more || !data.next_cursor) break;
    cursor = data.next_cursor;
  }
  return cards;
}

/** BFF turns Intel NOT_FOUND into HTTP 502. A deleted note still counts as gone. */
export function isNoteAlreadyGone(error: unknown): boolean {
  const message = error instanceof Error ? error.message : "";
  const status = error && typeof error === "object" && "status" in error ? (error as { status?: unknown }).status : undefined;
  if (status === 404) return true;
  return /not found/i.test(message) || /"NOT_FOUND"/.test(message);
}

/** Delete errors that still matter: the note is present in the follow-up note_list. */
export function noteDeletesStillLive(failedIds: readonly string[], liveIds: readonly string[]): string[] {
  const live = new Set(liveIds);
  return failedIds.filter((id) => live.has(id));
}

async function deleteDebriefNotes(contactId: string, notes: DebriefNote[], actor: string): Promise<void> {
  const failed: { id: string; error: unknown }[] = [];
  for (const note of notes) {
    try {
      await callTool("note_delete", {
        note_id: note.id,
        contact_id: contactId,
        actor,
        lineage: ratingLineage(),
      });
    } catch (error) {
      if (!isNoteAlreadyGone(error)) failed.push({ id: note.id, error });
    }
  }
  if (!failed.length) return;
  let liveIds: string[] = [];
  try {
    const listed = await callTool<{ results?: NoteHit[] }>("note_list", { contact_id: contactId, limit: 50 });
    liveIds = asNotes(listed.results ?? []).map((note) => note.id);
  } catch {
    throw failed[0].error;
  }
  const stillThere = noteDeletesStillLive(
    failed.map((item) => item.id),
    liveIds,
  );
  if (!stillThere.length) return;
  const blocker = failed.find((item) => item.id === stillThere[0]);
  throw blocker?.error ?? failed[0].error;
}

function asNotes(rows: NoteHit[]): DebriefNote[] {
  return rows.flatMap((row) => {
    const body = text(row.body);
    const id = text(row.note_id);
    if (!body || !id) return [];
    const authorId = text(row.actor) || "unknown";
    return [
      {
        id,
        body,
        authorId,
        authorName: firstNameFromUserKey(authorId),
        createdAt: text(row.created_at) || new Date().toISOString(),
      },
    ];
  });
}

function dueDate(value: unknown): string | null {
  const due = text(value);
  return /^\d{4}-\d{2}-\d{2}/.test(due) ? due.slice(0, 10) : null;
}

export function asFollowups(rows: FollowupHit[]): DebriefFollowup[] {
  return rows.flatMap((row) => {
    const status = text(row.status);
    if (status && status !== "open") return [];
    const title = text(row.title);
    const followupId = text(row.followup_id);
    if (!title || !followupId) return [];
    return [
      {
        followupId,
        title,
        dueAt: dueDate(row.due_at),
        createdAt: text(row.created_at) || new Date().toISOString(),
      },
    ];
  });
}

interface CardImageHit {
  url?: string | null;
}

interface BusinessCardHit {
  side?: string | null;
  processed?: CardImageHit | null;
  original?: CardImageHit | null;
  captured_at?: string | null;
}

function httpsUrl(value: unknown): string | null {
  const url = text(value);
  if (!url) return null;
  try {
    return new URL(url).protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

function cardScanUrl(card: BusinessCardHit | null | undefined): string | null {
  if (!card) return null;
  return httpsUrl(card.processed?.url) ?? httpsUrl(card.original?.url);
}

/** Latest https scan for each side. Processed image wins over the original. */
export function businessCardUrls(cards: BusinessCardHit[] | null | undefined): { front: string | null; back: string | null } {
  const list = [...(cards ?? [])].sort((a, b) => text(b.captured_at).localeCompare(text(a.captured_at)));
  return {
    front: cardScanUrl(list.find((card) => card.side !== "back" && cardScanUrl(card))),
    back: cardScanUrl(list.find((card) => card.side === "back" && cardScanUrl(card))),
  };
}

/** Front scan, or the back when the contact has no https front. */
export function businessCardPatch(
  cards: BusinessCardHit[] | null | undefined,
): Pick<DebriefCard, "businessCardUrl" | "businessCardFrontUrl" | "businessCardBackUrl"> {
  const scans = businessCardUrls(cards);
  return {
    businessCardUrl: scans.front ?? scans.back,
    businessCardFrontUrl: scans.front,
    businessCardBackUrl: scans.back,
  };
}

/** Mint a fresh read URL. contact_get SAS links expire, so the open card must not keep the first one. */
export async function refreshBusinessCard(
  contactId: string,
  eventKey: string,
): Promise<Pick<DebriefCard, "businessCardUrl" | "businessCardFrontUrl" | "businessCardBackUrl"> | null> {
  const photo = await callTool<{ business_cards?: BusinessCardHit[] | null }>("contact_get", {
    contact_id: contactId,
    campaign_key: eventKey,
  }).catch(() => null);
  if (!photo) return null;
  return businessCardPatch(photo.business_cards);
}

export function asLeadRating(value: unknown): LeadRating {
  const rating = typeof value === "number" ? value : Number(value);
  if (rating === 1 || rating === 2 || rating === 3 || rating === 4 || rating === 5) return rating;
  return 0;
}

export function familiarUserIdsFrom(rows: { user?: unknown }[] | null | undefined): string[] {
  const ids: string[] = [];
  for (const row of rows ?? []) {
    const user = text(row.user);
    if (user && !ids.some((id) => userIdsMatch(id, user))) ids.push(user);
  }
  return ids;
}

export async function hydrateContact(card: DebriefCard, eventKey: string): Promise<Partial<DebriefCard>> {
  const [notes, followups, photo, account] = await Promise.all([
    callTool<{ results?: NoteHit[] }>("note_list", { contact_id: card.contactId, limit: 50 }).catch(() => null),
    callTool<{ results?: FollowupHit[] }>("followup_list", {
      event_key: eventKey,
      contact_id: card.contactId,
      status: "open",
      limit: 50,
    }).catch(() => null),
    callTool<{
      photo?: { url?: string | null } | null;
      business_cards?: BusinessCardHit[] | null;
      familiar_users?: { user?: string | null }[] | null;
      key_contact?: boolean;
    }>("contact_get", {
      contact_id: card.contactId,
      campaign_key: eventKey,
    }).catch(() => null),
    card.accountId
      ? callTool<{
          strategic_priorities?: unknown;
          lead_rating?: unknown;
          priority?: boolean;
          owner_user?: string | null;
          account?: { strategic_priorities?: unknown; lead_rating?: unknown; priority?: boolean; owner_user?: string | null };
        }>("account_get", {
          account_id: card.accountId,
        }).catch(() => null)
      : Promise.resolve(null),
  ]);
  const patch: Partial<DebriefCard> = {};
  if (notes) patch.notes = asNotes(notes.results ?? []);
  if (followups) patch.followups = asFollowups(followups.results ?? []);
  const photoUrl = text(photo?.photo?.url);
  if (photoUrl) patch.photoUrl = photoUrl;
  if (photo) {
    Object.assign(patch, businessCardPatch(photo.business_cards));
    patch.familiarUserIds = familiarUserIdsFrom(photo.familiar_users);
    if (photo.key_contact !== undefined) patch.keyContact = photo.key_contact === true;
  }
  if (account) {
    const priorities = account.strategic_priorities ?? account.account?.strategic_priorities;
    patch.priorityIds = priorityIdsFromJson(priorities);
    patch.rating = asLeadRating(account.lead_rating ?? account.account?.lead_rating);
    if (account.priority !== undefined || account.account?.priority !== undefined) {
      patch.priorityFollowup = (account.priority ?? account.account?.priority) === true;
    }
    if (account.owner_user !== undefined || account.account?.owner_user !== undefined) {
      patch.ownerUserId = text(account.owner_user ?? account.account?.owner_user) || null;
    }
  }
  return patch;
}

export async function saveDebriefPatch(
  card: DebriefCard,
  patch: DebriefPatch,
  actor: string,
  eventKey: string,
): Promise<Partial<DebriefCard>> {
  const applied: Partial<DebriefCard> = {};
  if (patch.notes) {
    const previous = new Set(card.notes.map((note) => note.id));
    const nextIds = new Set(patch.notes.map((note) => note.id));
    const removed = card.notes.filter((note) => !nextIds.has(note.id));
    await deleteDebriefNotes(card.contactId, removed, actor);
    // Intel has note_add and note_delete. A body change is stored as a replacement note.
    const priorById = new Map(card.notes.map((note) => [note.id, note]));
    const edited = patch.notes.filter((note) => {
      const prior = priorById.get(note.id);
      const body = note.body.trim();
      return Boolean(prior && body && prior.body.trim() !== body);
    });
    for (const note of edited) {
      await deleteDebriefNotes(card.contactId, [note], actor);
      await callTool("note_add", {
        contact_id: card.contactId,
        account_id: card.accountId || undefined,
        body: note.body.trim(),
        actor,
        lineage: ratingLineage(),
        source: "debrief",
      });
    }
    const added = patch.notes.filter((note) => note.body.trim() && !previous.has(note.id));
    for (const note of added) {
      await callTool("note_add", {
        contact_id: card.contactId,
        account_id: card.accountId || undefined,
        body: note.body.trim(),
        actor,
        lineage: ratingLineage(),
        source: "debrief",
      });
    }
    const listed = await callTool<{ results?: NoteHit[] }>("note_list", { contact_id: card.contactId, limit: 50 });
    applied.notes = asNotes(listed.results ?? []);
  }
  if (patch.followups) {
    applied.followups = await saveFollowups(card, patch.followups, actor, eventKey);
  }
  if (patch.priorityIds !== undefined && card.accountId && card.accountName) {
    await callTool("account_upsert", {
      account_id: card.accountId,
      name: card.accountName,
      strategic_priorities: priorityLabels(patch.priorityIds),
      actor,
      lineage: ratingLineage(),
      source: "debrief",
    });
    applied.priorityIds = patch.priorityIds;
  }
  if (patch.segment !== undefined && card.accountId) {
    await saveSegment(card.accountId, patch.segment, actor);
  }
  if (patch.rating !== undefined) {
    if (!card.accountId || !card.accountName) {
      throw new Error("lead rating needs an account");
    }
    await callTool("account_upsert", {
      account_id: card.accountId,
      name: card.accountName,
      lead_rating: patch.rating,
      actor,
      lineage: ratingLineage(),
      source: "debrief",
    });
    applied.rating = patch.rating;
  }
  if (patch.familiarUserIds) {
    await callTool("contact_tag_put", {
      contact_id: card.contactId,
      familiar: patch.familiarUserIds.some((id) => userIdsMatch(id, actor)),
      actor,
      lineage: ratingLineage(),
    });
    applied.familiarUserIds = patch.familiarUserIds;
  }
  const accountFields: { priority?: boolean; owner_user?: string } = {};
  if (patch.priorityFollowup !== undefined) accountFields.priority = patch.priorityFollowup;
  if (patch.ownerUserId !== undefined) accountFields.owner_user = patch.ownerUserId ?? "";
  if (Object.keys(accountFields).length > 0) {
    if (!card.accountId || !card.accountName) {
      throw new Error("priority and owner need an account");
    }
    await callTool("account_upsert", {
      account_id: card.accountId,
      name: card.accountName,
      ...accountFields,
      actor,
      lineage: ratingLineage(),
      source: "debrief",
    });
    if (patch.priorityFollowup !== undefined) applied.priorityFollowup = patch.priorityFollowup;
    if (patch.ownerUserId !== undefined) applied.ownerUserId = patch.ownerUserId || null;
  }
  if (patch.keyContact !== undefined) {
    await callTool("contact_tag_put", {
      contact_id: card.contactId,
      key_contact: patch.keyContact,
      actor,
      lineage: ratingLineage(),
    });
    applied.keyContact = patch.keyContact;
  }
  return applied;
}

function serverFollowupId(id: string | null): string | null {
  if (!id || id.startsWith("local:")) return null;
  return id;
}

async function saveFollowups(
  card: DebriefCard,
  next: DebriefFollowup[],
  actor: string,
  eventKey: string,
): Promise<DebriefFollowup[]> {
  const priorById = new Map(
    card.followups.flatMap((item) => {
      const id = serverFollowupId(item.followupId);
      return id ? [[id, item] as const] : [];
    }),
  );
  const nextIds = new Set(
    next.flatMap((item) => {
      const id = serverFollowupId(item.followupId);
      return id ? [id] : [];
    }),
  );
  const removed = [...priorById.values()].filter((item) => {
    const id = serverFollowupId(item.followupId);
    return Boolean(id && !nextIds.has(id));
  });
  for (const item of removed) {
    await callTool("followup_upsert", {
      event_key: eventKey,
      followup_id: serverFollowupId(item.followupId),
      contact_id: card.contactId,
      account_id: card.accountId || undefined,
      title: item.title.trim() || "Follow-up",
      due_at: item.dueAt,
      status: "cancelled",
      actor,
      lineage: ratingLineage(),
      source: "debrief",
    });
  }
  for (const item of next) {
    const title = item.title.trim();
    if (!title) continue;
    const serverId = serverFollowupId(item.followupId);
    const prior = serverId ? priorById.get(serverId) : undefined;
    if (prior && prior.title.trim() === title && prior.dueAt === item.dueAt) continue;
    await callTool("followup_upsert", {
      event_key: eventKey,
      followup_id: prior ? serverId ?? undefined : undefined,
      contact_id: card.contactId,
      account_id: card.accountId || undefined,
      title,
      due_at: item.dueAt,
      status: "open",
      actor,
      lineage: ratingLineage(),
      source: "debrief",
    });
  }
  const listed = await callTool<{ results?: FollowupHit[] }>("followup_list", {
    event_key: eventKey,
    contact_id: card.contactId,
    status: "open",
    limit: 50,
  });
  return asFollowups(listed.results ?? []);
}

async function saveSegment(accountId: string, segment: string, actor: string): Promise<void> {
  let current: Record<string, unknown> = {};
  try {
    const got = await callTool<{ classification?: Record<string, unknown> | null }>("classification_get", {
      account_id: accountId,
    });
    if (got.classification && typeof got.classification === "object") current = got.classification;
  } catch {
    current = {};
  }
  const segment_primary = segment.trim() || null;
  await callTool("classification_upsert", {
    account_id: accountId,
    classification: { ...current, segment_primary },
    actor,
    lineage: ratingLineage(),
    source: "debrief",
  });
}

export async function addContactToShow(
  contactId: string,
  campaignKey: string,
  actor: string,
): Promise<{ eventContactId: number | null; eventContactDisplay: string | null }> {
  const journey = await callTool<{ event_contact_no?: number | string | null; event_contact_display?: string | null }>(
    "campaign_journey_put",
    {
      contact_id: contactId,
      campaign_key: campaignKey,
      actor,
      lineage: ratingLineage(),
      activities: {},
    },
  );
  return {
    eventContactId: eventContactNo(journey.event_contact_no),
    eventContactDisplay: text(journey.event_contact_display) || null,
  };
}
